import { BUILDERS_ROOT, GUIDES_AUTHORS_ROOT, GUIDES_ROOT, generateRoutes, type RouteBlueprint } from '@/plugins/router/localized.ts';
import { determineLocale } from '@/utilities';

const routeBlueprints: RouteBlueprint[] = [
  {
    name: 'home',
    path: { 'en-CA': '/', 'fr-CA': '/' },
    component: () => import('@/views/home/HomeView.vue'),
  },
  {
    name: 'project',
    path: { 'en-CA': '/projects/:slug-:projectId(\\d+)', 'fr-CA': '/projets/:slug-:projectId(\\d+)' },
    component: () => import('@/views/projects/ProjectView.vue'),
    props: (route) => ({ projectId: Number(route.params.projectId) }),
  },
  {
    name: 'directory',
    path: { 'en-CA': '/directory', 'fr-CA': '/repertoire' },
    component: () => import('@/views/directory/DirectoryView.vue'),
  },
  {
    name: 'builder',
    path: { 'en-CA': '/directory/:slug-:builderId(\\d+)', 'fr-CA': '/repertoire/:slug-:builderId(\\d+)' },
    component: () => import('@/views/directory/BuilderView.vue'),
    props: (route) => ({ builderId: Number(route.params.builderId) }),
  },
  {
    name: 'search',
    path: { 'en-CA': '/search', 'fr-CA': '/recherche' },
    component: () => import('@/views/search/SearchView.vue'),
  },
  {
    name: 'support',
    path: { 'en-CA': '/support', 'fr-CA': '/soutien' },
    component: () => import('@/views/SupportView.vue'),
  },
  {
    name: 'advertise',
    path: { 'en-CA': '/advertise', 'fr-CA': '/affichez-vous' },
    component: () => import('@/views/advertise/AdvertiseView.vue'),
  },
  {
    name: 'about',
    path: { 'en-CA': '/about', 'fr-CA': '/a-propos' },
    component: () => import('@/views/AboutView.vue'),
  },
  {
    name: 'privacy',
    path: { 'en-CA': '/privacy', 'fr-CA': '/confidentialite' },
    component: () => import('@/views/PrivacyView.vue'),
  },
  {
    name: 'terms',
    path: { 'en-CA': '/terms', 'fr-CA': '/conditions' },
    component: () => import('@/views/TermsView.vue'),
  },
  {
    name: 'profile',
    path: { 'en-CA': '/profile', 'fr-CA': '/profile' },
    component: () => import('@/views/profile/ProfileView.vue'),
  },
  {
    name: 'profile-login',
    path: { 'en-CA': '/profile/login', 'fr-CA': '/profile/login' },
    component: () => import('@/views/profile/ProfileLoginView.vue'),
  },
  {
    name: 'profile-login-error',
    path: { 'en-CA': '/profile/login/error', 'fr-CA': '/profile/login/error' },
    component: () => import('@/views/profile/ProfileLoginErrorView.vue'),
  },
  {
    name: 'profile-subscriptions',
    path: { 'en-CA': '/profile/subscriptions', 'fr-CA': '/profile/subscriptions' },
    component: () => import('@/views/profile/ProfileSubscriptionsView.vue'),
  },
  {
    name: 'editorial',
    path: { 'en-CA': '/editorial', 'fr-CA': '/editorial' },
    component: () => import('@/views/editorial/EditorialView.vue'),
  },
  {
    name: 'editorial-category',
    path: { 'en-CA': '/editorial/:categorySlug', 'fr-CA': '/editorial/:categorySlug' },
    component: () => import('@/views/editorial/EditorialCategoryView.vue'),
    props: (route) => ({ categorySlug: route.params.categorySlug }),
  },
  {
    name: 'editorial-location',
    path: { 'en-CA': '/editorial/:categorySlug/:locationSlug', 'fr-CA': '/editorial/:categorySlug/:locationSlug' },
    component: () => import('@/views/editorial/EditorialLocationView.vue'),
    props: (route) => ({ categorySlug: route.params.categorySlug, locationSlug: route.params.locationSlug }),
  },
  {
    name: 'editorial-child-location',
    path: { 'en-CA': '/editorial/:categorySlug/:parentLocationSlug/:childLocationSlug', 'fr-CA': '/editorial/:categorySlug/:parentLocationSlug/:childLocationSlug' },
    component: () => import('@/views/editorial/EditorialChildLocationView.vue'),
    props: (route) => ({ categorySlug: route.params.categorySlug, parentLocationSlug: route.params.parentLocationSlug, childLocationSlug: route.params.childLocationSlug }),
  },
  {
    name: 'builders',
    path: { 'en-CA': BUILDERS_ROOT['en-CA'], 'fr-CA': BUILDERS_ROOT['fr-CA'] },
    component: () => import('@/views/builders/BuildersView.vue'),
  },
  {
    name: 'builders-location',
    path: { 'en-CA': `${BUILDERS_ROOT['en-CA']}/:categorySlug/:locationSlug`, 'fr-CA': `${BUILDERS_ROOT['fr-CA']}/:categorySlug/:locationSlug` },
    component: () => import('@/views/builders/BuildersLocationView.vue'),
    props: (route) => ({ categorySlug: route.params.categorySlug, locationSlug: route.params.locationSlug }),
  },
  {
    name: 'ad-unit',
    path: { 'en-CA': '/ads/:adUnitId', 'fr-CA': '/pubs/:adUnitId' },
    component: () => import('@/views/advertise/AdUnitView.vue'),
    props: (route) => ({ adUnitId: Number(atob(route.params.adUnitId as string)) }),
  },
  {
    name: 'article',
    path: { 'en-CA': '/guide/:slug-:articleId(\\d+)', 'fr-CA': '/guide/:slug-:articleId(\\d+)' },
    component: () => import('@/views/articles/ArticleView.vue'),
    props: (route) => ({ articleId: Number(route.params.articleId) }),
  },
  {
    name: 'guides',
    path: { 'en-CA': GUIDES_ROOT['en-CA'], 'fr-CA': GUIDES_ROOT['fr-CA'] },
    component: () => import('@/views/guides/GuidesView.vue'),
  },
  {
    name: 'guides-author',
    path: { 'en-CA': `${GUIDES_AUTHORS_ROOT['en-CA']}/:authorSlug`, 'fr-CA': `${GUIDES_AUTHORS_ROOT['fr-CA']}/:authorSlug` },
    component: () => import('@/views/guides/GuideAuthorView.vue'),
    props: (route) => ({ authorSlug: route.params.authorSlug }),
  },
  {
    name: 'guides-category',
    path: { 'en-CA': `${GUIDES_ROOT['en-CA']}/:categorySlug`, 'fr-CA': `${GUIDES_ROOT['fr-CA']}/:categorySlug` },
    component: () => import('@/views/guides/GuideCategoryView.vue'),
    props: (route) => ({ categorySlug: route.params.categorySlug }),
  },
  {
    name: 'not-found',
    path: { 'en-CA': '/:pathMatch(.*)*', 'fr-CA': '/:pathMatch(.*)*' },
    component: () => import('@/views/NotFoundView.vue'),
  },
];

const locale = determineLocale();
export const routes = generateRoutes(routeBlueprints, locale);
